/**
 * The errands — the core loop's mini-quests (RC2-01).
 *
 * An errand is the *invitation*, never the teacher: the Elder speaks of a wolf
 * at the pens and you take it on, but you do not receive *tracking* for saying
 * yes — you receive it by reading the track. So this module grants no skill and
 * no reward on accept. What it does is small and honest: it records which
 * errands a character has taken on, and it closes them by **detecting the
 * ordinary world event that fulfils them** off the stream the game already
 * emits — a plant foraged, a wolf killed, a wound bound, a fire got going. The
 * same events `chronicle.ts` reads for the valley's memory and RC2-02's tallies
 * read for practice.
 *
 * That is the whole discipline, and it is `chronicle.ts`'s discipline applied to
 * quests: an errand cannot close without something having happened to close it,
 * because the only path to completion is a matching `SimEvent`. There is no
 * quest flag an NPC can set and no callback hidden in content. An errand is a
 * serialisable data record authored in `@annwn/content`: event-backed
 * objectives, composable availability rules, and facts resolved from current
 * world state. Dialogue offers that data and the HUD projects it; neither owns
 * a second copy of quest logic.
 *
 * **Where the tracker lives.** Beside `lore` in world state, keyed by actor id,
 * carried in and out of the character record by `WorldSession` exactly as `lore`
 * is — the model this deliberately follows (`item.ts`'s `Lore`). It is small,
 * bounded and JSON by construction, which is what anything living in world state
 * that survives a logout has to be.
 */
import { addItem, createInventory } from './item.js';
import { gameTime } from './time.js';
export function createActorErrands() {
    return { active: [], completed: [], graduated: false };
}
/* -------------------------------------------------------------------------- *
 * Taking one on
 * -------------------------------------------------------------------------- */
/**
 * Offer an errand to a character.
 *
 * Records it as active and returns the archived `offered` event, or `null` when
 * they already hold it or have already done it — offering is idempotent, so a
 * villager who repeats themselves does not double-book the errand. **No reward
 * and no skill is applied**: accepting only records where to go.
 */
export function offerErrand(world, actorId, def, tick) {
    const state = (world.errands[actorId] ??= createActorErrands());
    if (holds(state, def.id))
        return null;
    state.active = [
        ...state.active,
        {
            defId: def.id,
            acceptedTick: tick,
            ...(def.objectives ? { progress: {} } : {}),
        },
    ];
    return {
        type: 'errand',
        actors: [actorId],
        refs: ['offered', def.id],
        audience: 'archive',
    };
}
/** Do they hold this errand, active or completed? */
function holds(state, defId) {
    return (state.active.some((one) => one.defId === defId) ||
        state.completed.some((one) => one.defId === defId));
}
/** A character's relationship to one definition. */
export function errandState(world, actorId, defId) {
    const state = world.errands[actorId];
    if (state?.completed.some((one) => one.defId === defId))
        return 'completed';
    if (state?.active.some((one) => one.defId === defId))
        return 'active';
    return 'unstarted';
}
/** Evaluate content's availability data against current, authoritative state. */
export function matchesErrandRule(world, catalogue, actorId, rule, tick) {
    switch (rule.kind) {
        case 'always':
            return true;
        case 'never':
            return false;
        case 'all':
            return rule.rules.every((one) => matchesErrandRule(world, catalogue, actorId, one, tick));
        case 'any':
            return rule.rules.some((one) => matchesErrandRule(world, catalogue, actorId, one, tick));
        case 'not':
            return !matchesErrandRule(world, catalogue, actorId, rule.rule, tick);
        case 'background':
            return world.combat[actorId]?.loadout.background === rule.id;
        case 'errand':
            return errandState(world, actorId, rule.id) === rule.state;
        case 'hour': {
            const hour = gameTime(tick).hour;
            return rule.from <= rule.until
                ? hour >= rule.from && hour < rule.until
                : hour >= rule.from || hour < rule.until;
        }
        case 'villager-alive': {
            const actor = villagerActor(world, rule.villager);
            return actor !== null && world.combat[actor.id]?.vitals.dead !== true;
        }
    }
}
/** Whether this unheld errand is eligible to be offered now. */
export function canOfferErrand(world, catalogue, actorId, def, tick) {
    if (errandState(world, actorId, def.id) !== 'unstarted')
        return false;
    const rule = def.offerWhen ??
        (def.background
            ? { kind: 'background', id: def.background }
            : { kind: 'always' });
    return matchesErrandRule(world, catalogue, actorId, rule, tick);
}
/** First eligible errand from this giver, in content order. */
export function nextErrandFrom(world, catalogue, actorId, giver, tick) {
    return (catalogue
        .errands?.()
        .find((def) => def.giver === giver && canOfferErrand(world, catalogue, actorId, def, tick)) ??
        null);
}
/* -------------------------------------------------------------------------- *
 * Closing them off the stream
 * -------------------------------------------------------------------------- */
/**
 * Offer one event to every character's errands, closing any it fulfils.
 *
 * Shaped like `remember`: called from the one emitter seam every event passes
 * through (`advance`'s `emit`, once the live wiring lands), so a rule never has
 * to remember to close its own errand. Returns an archived `completed` event for
 * each errand it closed — the telemetry rows RC2-10 counts — so the caller can
 * emit them onto the same stream.
 *
 * The trigger is tested against the event *for the errand's owner*, which is why
 * this walks owners rather than trusting the event's first actor: a `kill` is
 * closed for the killer (`actors[1]`), a forage for the forager (`actors[0]`).
 */
export function closeErrands(world, defs, event, catalogue) {
    const closed = [];
    for (const [actorId, state] of Object.entries(world.errands)) {
        if (state.active.length === 0)
            continue;
        const remaining = [];
        const continuations = [];
        let activeChanged = false;
        for (const errand of state.active) {
            const def = defs.get(errand.defId);
            const objectives = def ? objectivesOf(def) : [];
            const progress = { ...(errand.progress ?? {}) };
            let changed = false;
            for (const objective of objectives) {
                const target = objective.count ?? 1;
                const current = progress[objective.id] ?? 0;
                if (current >= target)
                    continue;
                const amount = triggerAmount(objective.trigger, event, actorId);
                if (amount <= 0)
                    continue;
                const updated = Math.min(target, current + amount);
                progress[objective.id] = updated;
                changed = true;
                // Only a completed objective proves its prerequisites. Credit the
                // authored implication at its full target, since this is evidence of
                // completion rather than another measured quantity.
                if (updated >= target) {
                    for (const impliedId of objective.implies ?? []) {
                        const implied = objectives.find((one) => one.id === impliedId);
                        if (!implied)
                            continue;
                        const impliedTarget = implied.count ?? 1;
                        if ((progress[impliedId] ?? 0) >= impliedTarget)
                            continue;
                        progress[impliedId] = impliedTarget;
                        changed = true;
                    }
                }
            }
            const complete = objectives.length > 0 &&
                objectives.every((objective) => (progress[objective.id] ?? 0) >= (objective.count ?? 1));
            if (def && complete) {
                activeChanged = true;
                state.completed = [
                    ...state.completed,
                    { defId: errand.defId, acceptedTick: errand.acceptedTick, closedTick: event.tick },
                ];
                closed.push({
                    type: 'errand',
                    actors: [actorId],
                    refs: ['completed', errand.defId],
                    audience: 'archive',
                });
                const reward = grantErrandReward(world, catalogue, actorId, def.reward, event.tick);
                if (reward)
                    closed.push(reward);
                const continuation = def.continuesTo ? defs.get(def.continuesTo) : undefined;
                if (continuation)
                    continuations.push(continuation);
            }
            else {
                if (changed)
                    activeChanged = true;
                remaining.push(changed ? { ...errand, progress } : errand);
            }
        }
        if (activeChanged)
            state.active = remaining;
        for (const continuation of continuations) {
            const offered = offerErrand(world, actorId, continuation, event.tick);
            if (offered)
                closed.push(offered);
        }
    }
    return closed;
}
/** Apply a completion reward once, at the same seam that records completion. */
function grantErrandReward(world, catalogue, actorId, reward, tick) {
    const itemId = reward?.itemId;
    if (!catalogue || !itemId)
        return null;
    const combatant = world.combat?.[actorId];
    const weapon = catalogue.weapon(itemId);
    const armour = catalogue.armour(itemId);
    const item = catalogue.item?.(itemId);
    let granted = false;
    if (weapon && combatant) {
        const held = combatant.loadout.weaponId;
        const unarmed = held.length === 0 || held === catalogue.defaultLoadout?.weaponId;
        // A progression reward must not silently replace a character's better
        // sidearm. The bow is the exception: it is the tool this lesson unlocks.
        if (weapon.ranged || unarmed || held === weapon.id) {
            combatant.loadout = {
                ...combatant.loadout,
                weaponId: weapon.id,
                ...(weapon.takesEdge ? { edge: 'keen' } : {}),
                ...(weapon.ranged ? { ammunition: reward.ammunition ?? 0 } : {}),
            };
            granted = true;
        }
    }
    if (armour && combatant && !combatant.loadout.armourIds.includes(armour.id)) {
        combatant.loadout = {
            ...combatant.loadout,
            armourIds: [...combatant.loadout.armourIds, armour.id],
        };
        granted = true;
    }
    // A held weapon is still a physical possession. Keep it in the inventory as
    // well as the loadout, matching bows found in the world and making the reward
    // visible in the satchel (and durable across sessions).
    if (item && world.inventories) {
        const inventory = (world.inventories[actorId] ??= createInventory());
        addItem(inventory, item.id, reward.count ?? 1, tick, item.stackable);
        granted = true;
    }
    if (!granted)
        return null;
    const name = weapon?.name ?? armour?.name ?? item?.name ?? itemId;
    const arrows = weapon?.ranged && (reward.ammunition ?? 0) > 0 ? ` and ${reward.ammunition} arrows` : '';
    return {
        type: 'errand',
        actors: [actorId],
        refs: ['reward', itemId],
        text: reward.text ?? `You receive ${name}${arrows}.`,
        audience: 'actor',
        to: actorId,
    };
}
/**
 * Does this event fulfil this trigger, for this owner?
 *
 * Each branch reads the real event a producer emits today (the ref shapes are
 * cited); `watch` has no producer yet and never matches until RC2-05 arms it.
 */
export function matchTrigger(trigger, event, owner) {
    return triggerAmount(trigger, event, owner) > 0;
}
/** How much one event contributes — event quantities remain authoritative. */
export function triggerAmount(trigger, event, owner) {
    switch (trigger.kind) {
        case 'forage':
            // forage.ts: interacted / ['forage','done', itemId, ...] / actors[0]=forager
            return event.type === 'interacted' &&
                event.refs[0] === 'forage' &&
                event.refs[1] === 'done' &&
                event.refs[2] === trigger.item &&
                event.actors[0] === owner
                ? positiveCount(event.refs[3])
                : 0;
        case 'bind-wound':
            // interact.ts: interacted / [verb,'done', woundId, ...] / actors[0]=treater
            return event.type === 'interacted' &&
                TREATMENT_VERBS.has(event.refs[0] ?? '') &&
                event.refs[1] === 'done' &&
                event.actors[0] === owner
                ? 1
                : 0;
        case 'kill':
            // beasts.ts: died / ['carcass', planId] / actors=[dead, killer]
            return event.type === 'died' &&
                event.refs[0] === 'carcass' &&
                event.refs[1] === trigger.species &&
                event.actors[1] === owner
                ? 1
                : 0;
        case 'fire-lit':
            // camp.ts: interacted / ['build','lit', id] / actors[0]=builder
            return event.type === 'interacted' &&
                event.refs[0] === 'build' &&
                event.refs[1] === 'lit' &&
                event.actors[0] === owner
                ? 1
                : 0;
        case 'butcher':
            // camp.ts emits `done` for the main meat and `yield` for additional
            // products such as hides. Both are authoritative butchery output.
            return event.type === 'interacted' &&
                event.refs[0] === 'butcher' &&
                (event.refs[1] === 'done' || event.refs[1] === 'yield') &&
                (trigger.product === undefined || event.refs[2] === trigger.product) &&
                event.actors[0] === owner
                ? positiveCount(event.refs[3])
                : 0;
        case 'cook':
            return event.type === 'interacted' &&
                event.refs[0] === 'cook' &&
                event.refs[1] === 'done' &&
                (trigger.product === undefined || event.refs[2] === trigger.product) &&
                event.actors[0] === owner
                ? positiveCount(event.refs[3])
                : 0;
        case 'prepare':
            return event.type === 'interacted' &&
                event.refs[0] === 'prepare' &&
                event.refs[1] === 'done' &&
                (trigger.product === undefined || event.refs[2] === trigger.product) &&
                event.actors[0] === owner
                ? 1
                : 0;
        case 'talk-to':
            return event.type === 'interacted' &&
                event.refs[0] === 'talk' &&
                event.refs[1] === 'done' &&
                event.refs[2] === trigger.villager &&
                event.actors[0] === owner
                ? 1
                : 0;
        case 'engage':
            return event.type === 'status' && event.refs[0] === 'engaged' && event.actors[0] === owner
                ? 1
                : 0;
        case 'disengage':
            return event.type === 'status' && event.refs[0] === 'disengaged' && event.actors[0] === owner
                ? 1
                : 0;
        case 'chop':
            // woodcraft.ts: interacted / ['chop','done', deadfallId] / actors[0]=chopper
            return event.type === 'interacted' &&
                event.refs[0] === 'chop' &&
                event.refs[1] === 'done' &&
                event.actors[0] === owner
                ? 1
                : 0;
        case 'watch':
            // A watch is the learnable combat pair: willingly enter, then get clear.
            return event.type === 'status' && event.refs[0] === 'disengaged' && event.actors[0] === owner
                ? 1
                : 0;
    }
}
function positiveCount(raw) {
    const parsed = Number(raw ?? 1);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}
/** Normalise compact legacy definitions into the objective engine. */
export function objectivesOf(def) {
    if (def.objectives)
        return def.objectives;
    return def.trigger ? [{ id: 'complete', text: def.prompt, trigger: def.trigger }] : [];
}
/** Current objective text and counts, for Journal/HUD projection. */
export function objectiveProgress(def, active) {
    return objectivesOf(def).map((objective) => ({
        id: objective.id,
        text: objective.text,
        current: Math.min(objective.count ?? 1, active.progress?.[objective.id] ?? 0),
        count: objective.count ?? 1,
    }));
}
/** Render authored speech plus facts read from the live world. */
export function renderErrandPrompt(world, catalogue, def, from) {
    const facts = (def.facts ?? []).map((fact) => {
        switch (fact.kind) {
            case 'villager-location':
                return villagerLocation(world, catalogue, fact.villager, from);
        }
    });
    return [def.prompt, ...facts].filter(Boolean).join(' ');
}
function villagerActor(world, defId) {
    const placed = Object.values(world.villagers).find((one) => one.defId === defId);
    return placed ? (world.actors[placed.actorId] ?? null) : null;
}
function villagerLocation(world, catalogue, defId, from) {
    const placed = Object.values(world.villagers).find((one) => one.defId === defId);
    const actor = placed ? world.actors[placed.actorId] : undefined;
    const def = catalogue.villager?.(defId) ?? null;
    const name = def?.name ?? actor?.name ?? defId;
    if (!placed || !actor)
        return `Nobody here knows where ${name} is.`;
    if (world.combat[actor.id]?.vitals.dead)
        return `${name} is dead.`;
    const nearest = Object.entries(placed.places)
        .map(([anchor, at]) => ({
        anchor,
        at,
        distance: Math.max(Math.abs(actor.x - at.x), Math.abs(actor.y - at.y)),
    }))
        .sort((left, right) => left.distance - right.distance || left.anchor.localeCompare(right.anchor))[0];
    const label = nearest
        ? (def?.placeNames?.[nearest.anchor] ?? `the ${nearest.anchor.replaceAll('-', ' ')}`)
        : null;
    if (nearest && nearest.distance <= 1 && label)
        return `${name} is at ${label}.`;
    const dx = actor.x - from.x;
    const dy = actor.y - from.y;
    const distance = Math.max(Math.abs(dx), Math.abs(dy));
    if (distance === 0)
        return `${name} is here.`;
    const direction = compass(dx, dy);
    return `${name} is ${distance} ${distance === 1 ? 'step' : 'steps'} ${direction} of here${label ? `, near ${label}` : ''}.`;
}
function compass(dx, dy) {
    const vertical = dy < 0 ? 'north' : dy > 0 ? 'south' : '';
    const horizontal = dx < 0 ? 'west' : dx > 0 ? 'east' : '';
    return vertical && horizontal ? `${vertical}-${horizontal}` : vertical || horizontal;
}
/** The treatment verbs whose `done` binds a wound (interact.ts). */
const TREATMENT_VERBS = new Set([
    'staunch',
    'bandage',
    'clean',
    'suture',
    'cauterize',
]);
/* -------------------------------------------------------------------------- *
 * Graduation
 * -------------------------------------------------------------------------- */
/**
 * The archived graduation event, once, when the first ring is complete.
 *
 * Owner decision (RC2-01): `audience: 'archive'` — invisible in play, recorded
 * so RC2-10 can measure who ran the loop. Nothing opens, because nothing was
 * closed; the beat is diegetic elsewhere. Returns `null` unless this call is the
 * one that newly completes the ring, and sets the flag so it never fires twice.
 *
 * `ringDefIds` is the set that *is* the first ring, supplied by the caller from
 * content — the sim does not hardcode which errands the glen's first ring holds.
 */
export function checkGraduation(world, actorId, ringDefIds) {
    const state = world.errands[actorId];
    if (!state || state.graduated)
        return null;
    const done = new Set(state.completed.map((one) => one.defId));
    if (!ringDefIds.every((id) => done.has(id)))
        return null;
    state.graduated = true;
    return {
        type: 'errand',
        actors: [actorId],
        refs: ['graduated'],
        audience: 'archive',
    };
}
/* -------------------------------------------------------------------------- *
 * Selecting from content
 * -------------------------------------------------------------------------- */
/**
 * The first errand for a background — the RC2-01 flex.
 *
 * Content-free: the caller passes the authored defs (`ERRANDS`), so the sim
 * stays readable by a world with no content in it. The first ring's errand whose
 * `background` matches, or `null` when that background has no doorway authored.
 */
export function firstErrandFor(defs, background) {
    return defs.find((def) => def.ring === 0 && def.background === background) ?? null;
}
/** The def ids that make up one ring, for `checkGraduation`. */
export function ringDefIds(defs, ring) {
    return defs.filter((def) => def.ring === ring).map((def) => def.id);
}
//# sourceMappingURL=errands.js.map